import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Save, Upload, User, Mail, Phone,
    Globe, Briefcase, Building, FileText, Loader2, Camera,
    Linkedin, Twitter, Facebook, Instagram, Github, Youtube,
    MessageSquare, Palette, Plus, X, GripVertical, Eye, EyeOff,
    ChevronDown, ChevronUp, Calendar, Link as LinkIcon, Heart, Contact
} from 'lucide-react';
import { apiMethods } from '@/services/api';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import AvatarEditor from 'react-avatar-editor';
import ThemePicker, { PhonePreview } from '@/components/card-editor/ThemePicker';
import LogoUpload from '@/components/card-editor/LogoUpload';
import { cardFromUser } from '@/features/card/lib/fromUser';
import { getTheme } from '@/features/card/themes';

const Profile = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        profile: {
            name: '',
            nickname: '',
            jobTitle: '',
            organization: '',
            organizationLogo: '',
            phone: '',
            email: '',
            biography: ''
        },
        socialMedia: {
            linkedin: '',
            x: '',
            facebook: '',
            instagram: '',
            youtube: '',
            github: '',
            website: '',
            tiktok: '',
            whatsapp: '',
            telegram: ''
        },
        cardTheme: 'default',
        cardAccentColor: null,
        cardSettings: { exchangeEnabled: true }
    });

    // New state for contacts and nonprofits
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [emailAddresses, setEmailAddresses] = useState([]);
    const [nonprofitOrganizations, setNonprofitOrganizations] = useState([]);

    // States for image editor
    const [showImageEditor, setShowImageEditor] = useState(false);
    const [imageToEdit, setImageToEdit] = useState(null);
    const [editorScale, setEditorScale] = useState(1.2);
    const [editorPosition, setEditorPosition] = useState({ x: 0.5, y: 0.5 });
    const editorRef = useRef(null);

    // UI state
    const [showPhoneForm, setShowPhoneForm] = useState(false);
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [showNonprofitForm, setShowNonprofitForm] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        phones: true,
        emails: true,
        nonprofits: true
    });

    const [profilePicturePreview, setProfilePicturePreview] = useState(null);
    const [errors, setErrors] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    const [newNonprofitLogo, setNewNonprofitLogo] = useState('');
    // Background refetches (after adding a phone, uploading a logo, ...) must not wipe unsaved form edits
    const dirtyRef = useRef(false);
    useEffect(() => {
        dirtyRef.current = isDirty;
    }, [isDirty]);

    // Phone type options
    const phoneTypes = [
        { value: 'mobile', label: 'Mobile' },
        { value: 'work', label: 'Work' },
        { value: 'home', label: 'Home' },
        { value: 'other', label: 'Other' }
    ];

    // Email type options
    const emailTypes = [
        { value: 'personal', label: 'Personal' },
        { value: 'work', label: 'Work' },
        { value: 'other', label: 'Other' }
    ];

    // Fetch current profile
    const { data, isLoading, isSuccess } = useQuery({
        queryKey: ['userProfile'],
        queryFn: async () => {
            const response = await apiMethods.users.getProfile();
            return response.data;
        }
    });

    // Populate form when data is loaded
    useEffect(() => {
        if (isSuccess && data?.user) {
            const user = data.user;

            if (!dirtyRef.current) setFormData({
                profile: {
                    name: user.profile?.name || '',
                    nickname: user.profile?.nickname || '',
                    jobTitle: user.profile?.jobTitle || '',
                    organization: user.profile?.organization || '',
                    organizationLogo: user.profile?.organizationLogo || '',
                    phone: user.profile?.phone || '',
                    email: user.profile?.email || user.email || '',
                    biography: user.profile?.biography || ''
                },
                socialMedia: {
                    linkedin: user.socialMedia?.linkedin || '',
                    x: user.socialMedia?.x || user.socialMedia?.twitter || '',
                    facebook: user.socialMedia?.facebook || '',
                    instagram: user.socialMedia?.instagram || '',
                    youtube: user.socialMedia?.youtube || '',
                    github: user.socialMedia?.github || '',
                    website: user.socialMedia?.website || '',
                    tiktok: user.socialMedia?.tiktok || '',
                    whatsapp: user.socialMedia?.whatsapp || '',
                    telegram: user.socialMedia?.telegram || ''
                },
                cardTheme: user.cardTheme || 'default',
                cardAccentColor: user.cardAccentColor || null,
                cardSettings: { exchangeEnabled: user.cardSettings?.exchangeEnabled !== false },
                isAccountEmailVisible: user.isAccountEmailVisible !== undefined ? user.isAccountEmailVisible : true
            });

            if (user.profile?.profilePicture) {
                setProfilePicturePreview(user.profile.profilePicture);
            }

            // Set new contact arrays
            setPhoneNumbers(user.phoneNumbers || []);
            setEmailAddresses(user.emailAddresses || []);
            setNonprofitOrganizations(user.nonprofitOrganizations || []);
        }
    }, [isSuccess, data]);

    // Update profile mutation
    const updateProfileMutation = useMutation({
        mutationFn: async (data) => {
            const response = await apiMethods.users.updateProfile(data);
            return response.data;
        },
        onSuccess: () => {
            dirtyRef.current = false;
            queryClient.invalidateQueries(['userProfile']);
            setIsDirty(false);
            showSuccessToast('Profile updated successfully!');
        },
        onError: (error) => {
            console.error('Update error:', error);
            showErrorToast(error.response?.data?.message || 'Failed to update profile');
        }
    });

    // Phone mutations
    const addPhoneMutation = useMutation({
        mutationFn: async (data) => {
            const response = await apiMethods.users.addPhone(data);
            return response.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['userProfile']);
            setPhoneNumbers([...phoneNumbers, data.phoneNumber]);
            setShowPhoneForm(false);
            showSuccessToast('Phone number added successfully!');
        },
        onError: (error) => {
            console.error('Add phone error:', error);
            showErrorToast(error.response?.data?.message || 'Failed to add phone number');
        }
    });

    const updatePhoneMutation = useMutation({
        mutationFn: async ({ id, data }) => {
            const response = await apiMethods.users.updatePhone(id, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['userProfile']);
            showSuccessToast('Phone number updated!');
        }
    });

    const deletePhoneMutation = useMutation({
        mutationFn: async (id) => {
            const response = await apiMethods.users.deletePhone(id);
            return response.data;
        },
        onSuccess: (_, phoneId) => {
            queryClient.invalidateQueries(['userProfile']);
            setPhoneNumbers(phoneNumbers.filter(p => p.id !== phoneId));
            showSuccessToast('Phone number removed!');
        }
    });

    // Email mutations
    const addEmailMutation = useMutation({
        mutationFn: async (data) => {
            console.log('Attempting to add email:', data);
            const response = await apiMethods.users.addEmail(data);
            return response.data;
        },
        onSuccess: (data) => {
            console.log('Email added successfully:', data);
            queryClient.invalidateQueries(['userProfile']);
            setEmailAddresses([...emailAddresses, data.emailAddress]);
            setShowEmailForm(false);
            showSuccessToast('Email address added successfully!');
        },
        onError: (error) => {
            console.error('Add email error:', error);
            console.error('Error response:', error.response);
            showErrorToast(error.response?.data?.message || 'Failed to add email address');
        }
    });

    const updateEmailMutation = useMutation({
        mutationFn: async ({ id, data }) => {
            const response = await apiMethods.users.updateEmail(id, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['userProfile']);
            showSuccessToast('Email address updated!');
        }
    });

    const deleteEmailMutation = useMutation({
        mutationFn: async (id) => {
            const response = await apiMethods.users.deleteEmail(id);
            return response.data;
        },
        onSuccess: (_, emailId) => {
            queryClient.invalidateQueries(['userProfile']);
            setEmailAddresses(emailAddresses.filter(e => e.id !== emailId));
            showSuccessToast('Email address removed!');
        }
    });

    // Nonprofit mutations
    const addNonprofitMutation = useMutation({
        mutationFn: async (data) => {
            const response = await apiMethods.users.addNonprofit(data);
            return response.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['userProfile']);
            setNonprofitOrganizations([...nonprofitOrganizations, data.nonprofitOrganization]);
            setShowNonprofitForm(false);
            setNewNonprofitLogo('');
            showSuccessToast('Nonprofit organization added successfully!');
        },
        onError: (error) => {
            console.error('Add nonprofit error:', error);
            showErrorToast(error.response?.data?.message || 'Failed to add nonprofit organization');
        }
    });

    const updateNonprofitMutation = useMutation({
        mutationFn: async ({ id, data }) => {
            const response = await apiMethods.users.updateNonprofit(id, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['userProfile']);
            showSuccessToast('Nonprofit organization updated!');
        }
    });

    const deleteNonprofitMutation = useMutation({
        mutationFn: async (id) => {
            const response = await apiMethods.users.deleteNonprofit(id);
            return response.data;
        },
        onSuccess: (_, nonprofitId) => {
            queryClient.invalidateQueries(['userProfile']);
            setNonprofitOrganizations(nonprofitOrganizations.filter(n => n.id !== nonprofitId));
            showSuccessToast('Nonprofit organization removed!');
        }
    });

    // Upload profile picture mutation
    const uploadPictureMutation = useMutation({
        mutationFn: async (file) => {
            const formData = new FormData();
            formData.append('profilePicture', file);

            const response = await apiMethods.users.uploadProfilePicture(formData);
            return response.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['userProfile']);
            if (data.profilePictureUrl) {
                setProfilePicturePreview(data.profilePictureUrl);
            }
        },
        onError: (error) => {
            const message = error.response?.data?.message || 'Failed to upload profile picture';
            setErrors({ profilePicture: message });
        }
    });

    // Toast functions
    const showSuccessToast = (message) => {
        const toast = document.createElement('div');
        toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300 ease-in-out';
        toast.innerHTML = `
            <div class="flex items-center">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                ${message}
            </div>
        `;
        document.body.appendChild(toast);

        setTimeout(() => {
            toast.style.opacity = '0';
            toast.style.transform = 'translateY(-20px)';
            setTimeout(() => toast.remove(), 300);
        }, 3000);
    };

    const showErrorToast = (message) => {
        const toast = document.createElement('div');
        toast.className = 'fixed top-4 right-4 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300 ease-in-out';
        toast.innerHTML = `
            <div class="flex items-center">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                ${message}
            </div>
        `;
        document.body.appendChild(toast);

        setTimeout(() => {
            toast.style.opacity = '0';
            toast.style.transform = 'translateY(-20px)';
            setTimeout(() => toast.remove(), 300);
        }, 3000);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const [section, field] = name.split('.');

        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));

        setIsDirty(true);

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleThemeChange = (theme) => {
        setFormData(prev => {
            // Accents belong to a theme; drop one the new theme doesn't offer (the server does the same)
            const accents = getTheme(theme).accents.map(a => a.toLowerCase());
            const keep = prev.cardAccentColor && accents.includes(prev.cardAccentColor.toLowerCase());
            return { ...prev, cardTheme: theme, cardAccentColor: keep ? prev.cardAccentColor : null };
        });
        setIsDirty(true);
    };

    const handleAccentChange = (accent) => {
        setFormData(prev => ({ ...prev, cardAccentColor: accent }));
        setIsDirty(true);
    };

    // Logos are saved right away (like the profile photo) so an upload is never lost
    const saveOrgLogo = async (url) => {
        try {
            await apiMethods.users.updateProfile({ profile: { organizationLogo: url } });
            setFormData(prev => ({ ...prev, profile: { ...prev.profile, organizationLogo: url } }));
            queryClient.invalidateQueries(['userProfile']);
            showSuccessToast(url ? 'Organization logo saved!' : 'Organization logo removed');
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'Failed to save logo');
        }
    };

    const saveNonprofitLogo = (nonprofit, index, url) => {
        const updated = [...nonprofitOrganizations];
        updated[index] = { ...updated[index], logo: url };
        setNonprofitOrganizations(updated);
        updateNonprofitMutation.mutate({ id: nonprofit.id, data: { logo: url } });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setErrors({ profilePicture: 'Please select an image file' });
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                setErrors({ profilePicture: 'Image must be less than 5MB' });
                return;
            }

            // Open editor instead of uploading directly
            const reader = new FileReader();
            reader.onload = (e) => {
                setImageToEdit(e.target.result);
                setShowImageEditor(true);
            };
            reader.readAsDataURL(file);

            setErrors(prev => ({ ...prev, profilePicture: '' }));
        }
    };

    // Function to handle the edited image
    const handleSaveEditedImage = async () => {
        if (editorRef.current) {
            // Get the canvas
            const canvas = editorRef.current.getImageScaledToCanvas();

            // Convert to blob
            canvas.toBlob(async (blob) => {
                // Create a File object from the blob
                const file = new File([blob], 'profile-picture.jpg', { type: 'image/jpeg' });

                // Upload using existing mutation
                uploadPictureMutation.mutate(file);

                // Close editor
                setShowImageEditor(false);
                setImageToEdit(null);
            }, 'image/jpeg', 0.9);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.profile.name) {
            newErrors['profile.name'] = 'Name is required';
        }

        if (!formData.profile.email) {
            newErrors['profile.email'] = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.profile.email)) {
            newErrors['profile.email'] = 'Email is invalid';
        }

        if (formData.profile.phone && !/^[\d\s\-\+\(\)]+$/.test(formData.profile.phone)) {
            newErrors['profile.phone'] = 'Phone number is invalid';
        }

        if (formData.profile.biography && formData.profile.biography.length > 2000) {
            newErrors['profile.biography'] = 'Biography cannot exceed 2000 characters';
        }

        // Validate social media URLs
        Object.entries(formData.socialMedia).forEach(([platform, url]) => {
            if (url && !isValidUrl(url)) {
                newErrors[`socialMedia.${platform}`] = 'Invalid URL';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isValidUrl = (url) => {
        if (!url) return true;
        try {
            const fullUrl = url.startsWith('http') ? url : `https://${url}`;
            new URL(fullUrl);
            return true;
        } catch {
            return false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            updateProfileMutation.mutate(formData);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleDragEnd = async (result, type) => {
        if (!result.destination) return;

        let items, setItems, reorderMutation;

        switch (type) {
            case 'phones':
                items = phoneNumbers;
                setItems = setPhoneNumbers;
                reorderMutation = apiMethods.users.reorderPhones;
                break;
            case 'emails':
                items = emailAddresses;
                setItems = setEmailAddresses;
                reorderMutation = apiMethods.users.reorderEmails;
                break;
            case 'nonprofits':
                items = nonprofitOrganizations;
                setItems = setNonprofitOrganizations;
                reorderMutation = apiMethods.users.reorderNonprofits;
                break;
            default:
                return;
        }

        const reorderedItems = Array.from(items);
        const [reorderedItem] = reorderedItems.splice(result.source.index, 1);
        reorderedItems.splice(result.destination.index, 0, reorderedItem);

        setItems(reorderedItems);

        // Update order on backend
        try {
            await reorderMutation({ [`${type}Ids`]: reorderedItems.map(item => item.id) });
        } catch (error) {
            console.error('Failed to reorder:', error);
            setItems(items); // Revert on error
        }
    };

    // Live preview: the saved card with this page's unsaved edits applied
    const previewCard = useMemo(() => {
        const user = data?.user;
        if (!user) return null;
        const visible = (list) => list.filter(x => x && x.isVisible !== false);
        return cardFromUser(user, {
            theme: formData.cardTheme,
            accentColor: formData.cardAccentColor,
            exchangeEnabled: formData.cardSettings?.exchangeEnabled !== false,
            profile: {
                ...(user.profile || {}),
                ...formData.profile,
                profilePicture: profilePicturePreview || user.profile?.profilePicture,
            },
            socialMedia: formData.socialMedia,
            accountEmail: formData.isAccountEmailVisible ? user.email : null,
            phoneNumbers: visible(phoneNumbers),
            emailAddresses: visible(emailAddresses),
            nonprofitOrganizations: visible(nonprofitOrganizations),
        });
    }, [data, formData, profilePicturePreview, phoneNumbers, emailAddresses, nonprofitOrganizations]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="animate-spin h-8 w-8 text-primary-600" />
            </div>
        );
    }

    const socialPlatforms = [
        { key: 'linkedin', label: 'LinkedIn', icon: Linkedin },
        { key: 'x', label: 'X (Twitter)', icon: Twitter },
        { key: 'facebook', label: 'Facebook', icon: Facebook },
        { key: 'instagram', label: 'Instagram', icon: Instagram },
        { key: 'github', label: 'GitHub', icon: Github },
        { key: 'youtube', label: 'YouTube', icon: Youtube },
        { key: 'website', label: 'Website', icon: LinkIcon },
        { key: 'tiktok', label: 'TikTok', icon: MessageSquare },
        { key: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
        { key: 'telegram', label: 'Telegram', icon: MessageSquare }
    ];

    // Calculate profile completeness percentage
    const getProfileCompleteness = () => {
        let completedFields = 0;
        let totalFields = 0;

        // Basic required fields
        const basicFields = [
            formData.profile.name,
            formData.profile.jobTitle,
            formData.profile.organization,
        ];
        totalFields += basicFields.length;
        completedFields += basicFields.filter(field => field && field.trim()).length;

        // Contact information (email required, phone optional)
        const hasEmail = emailAddresses.some(e => e.email && e.email.trim());
        totalFields += 1;
        if (hasEmail) completedFields += 1;

        // Profile picture
        const hasPicture = data?.user?.profile?.profilePicture || profilePicturePreview;
        totalFields += 1;
        if (hasPicture) completedFields += 1;

        // Social media/website
        const hasSocial = Object.values(formData.socialMedia).some(url => url && url.trim());
        totalFields += 1;
        if (hasSocial) completedFields += 1;

        // Biography
        const hasBio = formData.profile.biography && formData.profile.biography.trim();
        totalFields += 1;
        if (hasBio) completedFields += 1;

        return Math.min((completedFields / totalFields) * 100, 100);
    };

    // Get missing profile fields for guidance
    const getMissingFields = () => {
        const missing = [];

        if (!formData.profile.name?.trim()) missing.push("Full Name");
        if (!formData.profile.jobTitle?.trim()) missing.push("Job Title");
        if (!formData.profile.organization?.trim()) missing.push("Organization");
        if (!emailAddresses.some(e => e.email && e.email.trim())) missing.push("Email Address");
        if (!data?.user?.profile?.profilePicture && !profilePicturePreview) missing.push("Profile Picture");
        if (!Object.values(formData.socialMedia).some(url => url && url.trim())) missing.push("Social Media or Website");
        if (!formData.profile.biography?.trim()) missing.push("Biography");

        return missing;
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl lg:max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Edit Profile</h1>
                            <p className="mt-2 text-gray-600">
                                Update your business card information
                            </p>
                        </div>
                        <Link
                            to="/user/card-preview"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 hidden sm:inline-flex"
                        >
                            <Eye className="h-4 w-4 mr-2" />
                            Preview Card
                        </Link>
                        {/* Mobile Preview Button */}
                        <Link
                            to="/user/card-preview"
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 sm:hidden"
                        >
                            <Eye className="h-4 w-4" />
                        </Link>
                    </div>

                    {/* Profile Completeness Indicator */}
                    <div className="mt-6 bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Profile Completeness</span>
                            <span className="text-sm text-gray-500">{Math.round(getProfileCompleteness())}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${getProfileCompleteness()}%` }}
                            ></div>
                        </div>
                        {getProfileCompleteness() < 100 ? (
                            <div className="mt-3">
                                <p className="text-xs text-gray-600 mb-2">
                                    To reach 100%, please add:
                                </p>
                                <div className="flex flex-wrap gap-1">
                                    {getMissingFields().map((field, index) => (
                                        <span
                                            key={field}
                                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-800"
                                        >
                                            {field}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <p className="text-xs text-green-600 mt-2 flex items-center">
                                <span className="mr-1">✓</span>
                                Your profile is complete! Great job.
                            </p>
                        )}
                    </div>
                </div>

                <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8 lg:items-start">
                <div className="min-w-0">
                <form onSubmit={handleSubmit} className="space-y-8">
                    {errors.general && (
                        <div className="rounded-md bg-red-50 p-4">
                            <p className="text-sm text-red-800">{errors.general}</p>
                        </div>
                    )}

                    {/* Profile Picture */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Profile Picture</h2>

                        <div className="flex items-center space-x-6">
                            <div className="relative">
                                {profilePicturePreview ? (
                                    <img
                                        src={profilePicturePreview}
                                        alt="Profile preview"
                                        className="h-24 w-24 rounded-full object-cover border-2 border-gray-200"
                                    />
                                ) : (
                                    <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center">
                                        <User className="h-12 w-12 text-gray-400" />
                                    </div>
                                )}
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="absolute bottom-0 right-0 bg-primary-600 p-2 rounded-full text-white hover:bg-primary-700 shadow-lg"
                                    disabled={uploadPictureMutation.isPending}
                                >
                                    {uploadPictureMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Camera className="h-4 w-4" />
                                    )}
                                </button>
                            </div>

                            <div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploadPictureMutation.isPending}
                                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <Upload className="h-4 w-4 inline mr-2" />
                                    {uploadPictureMutation.isPending ? 'Uploading...' : 'Change Photo'}
                                </button>
                                <p className="mt-2 text-xs text-gray-500">
                                    JPG, PNG or GIF. Max size 5MB.
                                </p>
                                {errors.profilePicture && (
                                    <p className="mt-1 text-sm text-red-600">{errors.profilePicture}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Basic Information */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>

                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                    Full Name *
                                </label>
                                <div className="mt-1 relative">
                                    <input
                                        type="text"
                                        name="profile.name"
                                        id="name"
                                        value={formData.profile.name}
                                        onChange={handleInputChange}
                                        className={`appearance-none block w-full px-3 py-2 border ${
                                            errors['profile.name'] ? 'border-red-300' : 'border-gray-300'
                                        } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                    />
                                    {errors['profile.name'] && (
                                        <p className="mt-1 text-sm text-red-600">{errors['profile.name']}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label htmlFor="nickname" className="block text-sm font-medium text-gray-700">
                                    Nickname
                                </label>
                                <div className="mt-1">
                                    <input
                                        type="text"
                                        name="profile.nickname"
                                        id="nickname"
                                        maxLength={40}
                                        value={formData.profile.nickname}
                                        onChange={handleInputChange}
                                        placeholder="e.g. Scorp"
                                        aria-describedby="nickname-help"
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    />
                                    <p id="nickname-help" className="mt-1 text-xs text-gray-500">
                                        Shown on your card, e.g. on the Skully top banner. If empty, a nickname in quotes in your name is used (Benjamin &ldquo;Scorp&rdquo; Young).
                                    </p>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="accountEmail" className="block text-sm font-medium text-gray-700">
                                    Account Email
                                </label>
                                <div className="mt-1 space-y-2">
                                    <input
                                        type="email"
                                        id="accountEmail"
                                        value={data?.user?.email || ''}
                                        disabled
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-600 cursor-not-allowed sm:text-sm"
                                    />
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={formData.isAccountEmailVisible}
                                            onChange={(e) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    isAccountEmailVisible: e.target.checked
                                                }));
                                                setIsDirty(true);
                                            }}
                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="text-sm text-gray-700">Show on card</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                    Primary Phone
                                </label>
                                <div className="mt-1">
                                    <input
                                        type="tel"
                                        name="profile.phone"
                                        id="phone"
                                        value={formData.profile.phone}
                                        onChange={handleInputChange}
                                        className={`appearance-none block w-full px-3 py-2 border ${
                                            errors['profile.phone'] ? 'border-red-300' : 'border-gray-300'
                                        } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                    />
                                    {errors['profile.phone'] && (
                                        <p className="mt-1 text-sm text-red-600">{errors['profile.phone']}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700">
                                    Job Title
                                </label>
                                <div className="mt-1">
                                    <input
                                        type="text"
                                        name="profile.jobTitle"
                                        id="jobTitle"
                                        value={formData.profile.jobTitle}
                                        onChange={handleInputChange}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="organization" className="block text-sm font-medium text-gray-700">
                                    Organization
                                </label>
                                <div className="mt-1">
                                    <input
                                        type="text"
                                        name="profile.organization"
                                        id="organization"
                                        value={formData.profile.organization}
                                        onChange={handleInputChange}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    />
                                </div>
                                <div className="mt-2">
                                    <LogoUpload
                                        label="Logo"
                                        size="sm"
                                        value={formData.profile.organizationLogo}
                                        onChange={saveOrgLogo}
                                    />
                                </div>
                            </div>

                            <div className="sm:col-span-2">
                                <label htmlFor="biography" className="block text-sm font-medium text-gray-700">
                                    Biography
                                    <span className={`ml-2 text-sm ${
                                        formData.profile.biography.length > 2000 ? 'text-red-600' : 'text-gray-500'
                                    }`}>
                                        ({formData.profile.biography.length}/2000)
                                    </span>
                                </label>
                                <div className="mt-1">
                                    <textarea
                                        name="profile.biography"
                                        id="biography"
                                        rows={6}
                                        maxLength={2000}
                                        value={formData.profile.biography}
                                        onChange={handleInputChange}
                                        className={`appearance-none block w-full px-3 py-2 border ${
                                            formData.profile.biography.length > 2000 ? 'border-red-300' : 'border-gray-300'
                                        } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                        placeholder="Tell us about yourself..."
                                    />
                                    {(formData.profile.biography.length > 2000 || errors['profile.biography']) && (
                                        <p className="mt-1 text-sm text-red-600">
                                            {errors['profile.biography'] || 'Biography must be 2000 characters or less'}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Card Theme Selection */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                            <Palette className="h-5 w-5 mr-2" />
                            Card Theme
                        </h2>
                        <p className="text-sm text-gray-600 mb-6">
                            Pick the look that matches your physical HexLink card. Each preview is your own card.
                        </p>

                        <ThemePicker
                            user={data?.user}
                            previewCard={previewCard}
                            themeId={formData.cardTheme}
                            accent={formData.cardAccentColor}
                            onThemeChange={handleThemeChange}
                            onAccentChange={handleAccentChange}
                        />
                    </div>

                    {/* Contact exchange */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 id="exchange-label" className="text-lg font-medium text-gray-900 flex items-center">
                                    <Contact className="h-5 w-5 mr-2" />
                                    Let visitors share their contact with me
                                </h2>
                                <p className="mt-1 text-sm text-gray-600">
                                    Adds a &ldquo;Share my contact&rdquo; button to your card. When someone uses it, you get an email and they appear in{' '}
                                    <Link to="/user/connections" className="text-primary-600 hover:text-primary-700 font-medium">Connections</Link>.
                                </p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={formData.cardSettings?.exchangeEnabled !== false}
                                aria-labelledby="exchange-label"
                                onClick={() => {
                                    setFormData(prev => ({
                                        ...prev,
                                        cardSettings: { ...prev.cardSettings, exchangeEnabled: prev.cardSettings?.exchangeEnabled === false }
                                    }));
                                    setIsDirty(true);
                                }}
                                className={`relative mt-1 inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                                    formData.cardSettings?.exchangeEnabled !== false ? 'bg-primary-600' : 'bg-gray-200'
                                }`}
                            >
                                <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                        formData.cardSettings?.exchangeEnabled !== false ? 'translate-x-[22px]' : 'translate-x-[2px]'
                                    }`}
                                />
                            </button>
                        </div>
                    </div>

                    {/* Social Media & Links */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Social Media & Links</h2>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            {socialPlatforms.map(({ key, label, icon: Icon }) => (
                                <div key={key}>
                                    <label htmlFor={key} className="block text-sm font-medium text-gray-700">
                                        <Icon className="inline h-4 w-4 mr-1" />
                                        {label}
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            type="text"
                                            name={`socialMedia.${key}`}
                                            id={key}
                                            value={formData.socialMedia[key]}
                                            onChange={handleInputChange}
                                            placeholder={key === 'website' ? 'https://example.com' : `https://${key}.com/username`}
                                            className={`appearance-none block w-full px-3 py-2 border ${
                                                errors[`socialMedia.${key}`] ? 'border-red-300' : 'border-gray-300'
                                            } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                        />
                                        {errors[`socialMedia.${key}`] && (
                                            <p className="mt-1 text-sm text-red-600">{errors[`socialMedia.${key}`]}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex items-center justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => navigate('/user/dashboard')}
                            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={updateProfileMutation.isPending || !isDirty}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {updateProfileMutation.isPending ? (
                                <>
                                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4 mr-2" />
                                    Save Changes
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {/* Phone Numbers - NOW OUTSIDE THE MAIN FORM */}
                <div className="bg-white shadow rounded-lg p-6 mt-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-medium text-gray-900 flex items-center">
                            <Phone className="h-5 w-5 mr-2" />
                            Phone Numbers
                        </h2>
                        <button
                            type="button"
                            onClick={() => toggleSection('phones')}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            {expandedSections.phones ? <ChevronUp /> : <ChevronDown />}
                        </button>
                    </div>

                    {expandedSections.phones && (
                        <>
                            <DragDropContext onDragEnd={(result) => handleDragEnd(result, 'phones')}>
                                <Droppable droppableId="phoneNumbers">
                                    {(provided) => (
                                        <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                                            {phoneNumbers.map((phone, index) => (
                                                <Draggable key={phone.id} draggableId={phone.id} index={index}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            className={`flex items-center space-x-3 p-3 bg-gray-50 rounded-lg ${
                                                                snapshot.isDragging ? 'shadow-lg' : ''
                                                            }`}
                                                        >
                                                            <div {...provided.dragHandleProps}>
                                                                <GripVertical className="h-5 w-5 text-gray-400" />
                                                            </div>

                                                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                                <input
                                                                    type="tel"
                                                                    value={phone.number}
                                                                    onChange={(e) => {
                                                                        const updated = [...phoneNumbers];
                                                                        updated[index].number = e.target.value;
                                                                        setPhoneNumbers(updated);
                                                                    }}
                                                                    onBlur={() => updatePhoneMutation.mutate({
                                                                        id: phone.id,
                                                                        data: { number: phone.number }
                                                                    })}
                                                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                                    placeholder="Phone number"
                                                                />

                                                                <select
                                                                    value={phone.type}
                                                                    onChange={(e) => {
                                                                        const updated = [...phoneNumbers];
                                                                        updated[index].type = e.target.value;
                                                                        setPhoneNumbers(updated);
                                                                        updatePhoneMutation.mutate({
                                                                            id: phone.id,
                                                                            data: { type: e.target.value }
                                                                        });
                                                                    }}
                                                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                                >
                                                                    {phoneTypes.map(type => (
                                                                        <option key={type.value} value={type.value}>
                                                                            {type.label}
                                                                        </option>
                                                                    ))}
                                                                </select>

                                                                {phone.type === 'other' && (
                                                                    <input
                                                                        type="text"
                                                                        value={phone.customLabel || ''}
                                                                        onChange={(e) => {
                                                                            const updated = [...phoneNumbers];
                                                                            updated[index].customLabel = e.target.value;
                                                                            setPhoneNumbers(updated);
                                                                        }}
                                                                        onBlur={() => updatePhoneMutation.mutate({
                                                                            id: phone.id,
                                                                            data: { customLabel: phone.customLabel }
                                                                        })}
                                                                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                                        placeholder="Custom label"
                                                                    />
                                                                )}
                                                            </div>

                                                            <div className="flex items-center space-x-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (!phone.isPrimary) {
                                                                            updatePhoneMutation.mutate({
                                                                                id: phone.id,
                                                                                data: { isPrimary: true }
                                                                            });
                                                                        }
                                                                    }}
                                                                    className={`px-3 py-1 text-xs rounded-md ${
                                                                        phone.isPrimary
                                                                            ? 'bg-primary-600 text-white'
                                                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                                    }`}
                                                                >
                                                                    Primary
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        updatePhoneMutation.mutate({
                                                                            id: phone.id,
                                                                            data: { isVisible: !phone.isVisible }
                                                                        });
                                                                    }}
                                                                    className="text-gray-500 hover:text-gray-700"
                                                                >
                                                                    {phone.isVisible ? (
                                                                        <Eye className="h-5 w-5" />
                                                                    ) : (
                                                                        <EyeOff className="h-5 w-5" />
                                                                    )}
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    onClick={() => deletePhoneMutation.mutate(phone.id)}
                                                                    className="text-red-500 hover:text-red-700"
                                                                >
                                                                    <X className="h-5 w-5" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>

                            {showPhoneForm ? (
                                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                    <form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            const formData = new FormData(e.target);
                                            addPhoneMutation.mutate({
                                                number: formData.get('number'),
                                                type: formData.get('type'),
                                                customLabel: formData.get('customLabel'),
                                                isPrimary: phoneNumbers.length === 0
                                            });
                                        }}
                                        className="space-y-3"
                                    >
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            <input
                                                type="tel"
                                                name="number"
                                                required
                                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                placeholder="Phone number"
                                            />

                                            <select
                                                name="type"
                                                defaultValue="mobile"
                                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                            >
                                                {phoneTypes.map(type => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>

                                            <input
                                                type="text"
                                                name="customLabel"
                                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                placeholder="Custom label (if other)"
                                            />
                                        </div>

                                        <div className="flex justify-end space-x-3">
                                            <button
                                                type="button"
                                                onClick={() => setShowPhoneForm(false)}
                                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={addPhoneMutation.isPending}
                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                                            >
                                                {addPhoneMutation.isPending ? (
                                                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                                ) : (
                                                    <Plus className="h-4 w-4 mr-2" />
                                                )}
                                                Add Phone
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setShowPhoneForm(true)}
                                    className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Phone Number
                                </button>
                            )}
                        </>
                    )}
                </div>

                {/* Email Addresses - NOW OUTSIDE THE MAIN FORM */}
                <div className="bg-white shadow rounded-lg p-6 mt-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-medium text-gray-900 flex items-center">
                            <Mail className="h-5 w-5 mr-2" />
                            Email Addresses
                        </h2>
                        <button
                            type="button"
                            onClick={() => toggleSection('emails')}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            {expandedSections.emails ? <ChevronUp /> : <ChevronDown />}
                        </button>
                    </div>

                    {expandedSections.emails && (
                        <>
                            <p className="text-sm text-gray-600 mb-4">
                                Your account email ({data?.user?.email}) is always your primary login email.
                            </p>

                            <DragDropContext onDragEnd={(result) => handleDragEnd(result, 'emails')}>
                                <Droppable droppableId="emailAddresses">
                                    {(provided) => (
                                        <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                                            {emailAddresses.map((email, index) => (
                                                <Draggable key={email.id} draggableId={email.id} index={index}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            className={`flex items-center space-x-3 p-3 bg-gray-50 rounded-lg ${
                                                                snapshot.isDragging ? 'shadow-lg' : ''
                                                            }`}
                                                        >
                                                            <div {...provided.dragHandleProps}>
                                                                <GripVertical className="h-5 w-5 text-gray-400" />
                                                            </div>

                                                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                                <input
                                                                    type="email"
                                                                    value={email.email}
                                                                    onChange={(e) => {
                                                                        const updated = [...emailAddresses];
                                                                        updated[index].email = e.target.value;
                                                                        setEmailAddresses(updated);
                                                                    }}
                                                                    onBlur={() => updateEmailMutation.mutate({
                                                                        id: email.id,
                                                                        data: { email: email.email }
                                                                    })}
                                                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                                    placeholder="Email address"
                                                                />

                                                                <select
                                                                    value={email.type}
                                                                    onChange={(e) => {
                                                                        const updated = [...emailAddresses];
                                                                        updated[index].type = e.target.value;
                                                                        setEmailAddresses(updated);
                                                                        updateEmailMutation.mutate({
                                                                            id: email.id,
                                                                            data: { type: e.target.value }
                                                                        });
                                                                    }}
                                                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                                >
                                                                    {emailTypes.map(type => (
                                                                        <option key={type.value} value={type.value}>
                                                                            {type.label}
                                                                        </option>
                                                                    ))}
                                                                </select>

                                                                {email.type === 'other' && (
                                                                    <input
                                                                        type="text"
                                                                        value={email.customLabel || ''}
                                                                        onChange={(e) => {
                                                                            const updated = [...emailAddresses];
                                                                            updated[index].customLabel = e.target.value;
                                                                            setEmailAddresses(updated);
                                                                        }}
                                                                        onBlur={() => updateEmailMutation.mutate({
                                                                            id: email.id,
                                                                            data: { customLabel: email.customLabel }
                                                                        })}
                                                                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                                        placeholder="Custom label"
                                                                    />
                                                                )}
                                                            </div>

                                                            <div className="flex items-center space-x-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (!email.isPrimary) {
                                                                            updateEmailMutation.mutate({
                                                                                id: email.id,
                                                                                data: { isPrimary: true }
                                                                            });
                                                                        }
                                                                    }}
                                                                    className={`px-3 py-1 text-xs rounded-md ${
                                                                        email.isPrimary
                                                                            ? 'bg-primary-600 text-white'
                                                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                                    }`}
                                                                >
                                                                    Primary
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        updateEmailMutation.mutate({
                                                                            id: email.id,
                                                                            data: { isVisible: !email.isVisible }
                                                                        });
                                                                    }}
                                                                    className="text-gray-500 hover:text-gray-700"
                                                                >
                                                                    {email.isVisible ? (
                                                                        <Eye className="h-5 w-5" />
                                                                    ) : (
                                                                        <EyeOff className="h-5 w-5" />
                                                                    )}
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    onClick={() => deleteEmailMutation.mutate(email.id)}
                                                                    className="text-red-500 hover:text-red-700"
                                                                >
                                                                    <X className="h-5 w-5" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>

                            {showEmailForm ? (
                                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                    <form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            const formData = new FormData(e.target);
                                            addEmailMutation.mutate({
                                                email: formData.get('email'),
                                                type: formData.get('type'),
                                                customLabel: formData.get('customLabel'),
                                                isPrimary: emailAddresses.length === 0
                                            });
                                        }}
                                        className="space-y-3"
                                    >
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            <input
                                                type="email"
                                                name="email"
                                                required
                                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                placeholder="Email address"
                                            />

                                            <select
                                                name="type"
                                                defaultValue="personal"
                                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                            >
                                                {emailTypes.map(type => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>

                                            <input
                                                type="text"
                                                name="customLabel"
                                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                placeholder="Custom label (if other)"
                                            />
                                        </div>

                                        <div className="flex justify-end space-x-3">
                                            <button
                                                type="button"
                                                onClick={() => setShowEmailForm(false)}
                                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={addEmailMutation.isPending}
                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                                            >
                                                {addEmailMutation.isPending ? (
                                                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                                ) : (
                                                    <Plus className="h-4 w-4 mr-2" />
                                                )}
                                                Add Email
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setShowEmailForm(true)}
                                    className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Email Address
                                </button>
                            )}
                        </>
                    )}
                </div>

                {/* Nonprofit Organizations - NOW OUTSIDE THE MAIN FORM */}
                <div className="bg-white shadow rounded-lg p-6 mt-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-medium text-gray-900 flex items-center">
                            <Heart className="h-5 w-5 mr-2" />
                            Nonprofit Organizations
                        </h2>
                        <button
                            type="button"
                            onClick={() => toggleSection('nonprofits')}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            {expandedSections.nonprofits ? <ChevronUp /> : <ChevronDown />}
                        </button>
                    </div>

                    {expandedSections.nonprofits && (
                        <>
                            <p className="text-sm text-gray-600 mb-4">
                                Add nonprofit organizations you're involved with to showcase your community involvement.
                            </p>

                            <DragDropContext onDragEnd={(result) => handleDragEnd(result, 'nonprofits')}>
                                <Droppable droppableId="nonprofitOrganizations">
                                    {(provided) => (
                                        <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                                            {nonprofitOrganizations.map((nonprofit, index) => (
                                                <Draggable key={nonprofit.id} draggableId={nonprofit.id} index={index}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            className={`p-4 bg-gray-50 rounded-lg ${
                                                                snapshot.isDragging ? 'shadow-lg' : ''
                                                            }`}
                                                        >
                                                            <div className="flex items-start space-x-3">
                                                                <div {...provided.dragHandleProps} className="mt-1">
                                                                    <GripVertical className="h-5 w-5 text-gray-400" />
                                                                </div>

                                                                <div className="flex-1 space-y-3">
                                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                                        <input
                                                                            type="text"
                                                                            value={nonprofit.name}
                                                                            onChange={(e) => {
                                                                                const updated = [...nonprofitOrganizations];
                                                                                updated[index].name = e.target.value;
                                                                                setNonprofitOrganizations(updated);
                                                                            }}
                                                                            onBlur={() => updateNonprofitMutation.mutate({
                                                                                id: nonprofit.id,
                                                                                data: { name: nonprofit.name }
                                                                            })}
                                                                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                                            placeholder="Organization name"
                                                                        />

                                                                        <input
                                                                            type="text"
                                                                            value={nonprofit.role || ''}
                                                                            onChange={(e) => {
                                                                                const updated = [...nonprofitOrganizations];
                                                                                updated[index].role = e.target.value;
                                                                                setNonprofitOrganizations(updated);
                                                                            }}
                                                                            onBlur={() => updateNonprofitMutation.mutate({
                                                                                id: nonprofit.id,
                                                                                data: { role: nonprofit.role }
                                                                            })}
                                                                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                                            placeholder="Your role (optional)"
                                                                        />
                                                                    </div>

                                                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                                        <input
                                                                            type="date"
                                                                            value={nonprofit.startDate ? new Date(nonprofit.startDate).toISOString().split('T')[0] : ''}
                                                                            onChange={(e) => {
                                                                                const updated = [...nonprofitOrganizations];
                                                                                updated[index].startDate = e.target.value;
                                                                                setNonprofitOrganizations(updated);
                                                                                updateNonprofitMutation.mutate({
                                                                                    id: nonprofit.id,
                                                                                    data: { startDate: e.target.value }
                                                                                });
                                                                            }}
                                                                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                                            placeholder="Start date"
                                                                        />

                                                                        <input
                                                                            type="date"
                                                                            value={nonprofit.endDate ? new Date(nonprofit.endDate).toISOString().split('T')[0] : ''}
                                                                            onChange={(e) => {
                                                                                const updated = [...nonprofitOrganizations];
                                                                                updated[index].endDate = e.target.value;
                                                                                updated[index].isCurrent = !e.target.value;
                                                                                setNonprofitOrganizations(updated);
                                                                                updateNonprofitMutation.mutate({
                                                                                    id: nonprofit.id,
                                                                                    data: {
                                                                                        endDate: e.target.value,
                                                                                        isCurrent: !e.target.value
                                                                                    }
                                                                                });
                                                                            }}
                                                                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                                            placeholder="End date"
                                                                        />

                                                                        <label className="flex items-center space-x-2">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={nonprofit.isCurrent}
                                                                                onChange={(e) => {
                                                                                    const updated = [...nonprofitOrganizations];
                                                                                    updated[index].isCurrent = e.target.checked;
                                                                                    if (e.target.checked) {
                                                                                        updated[index].endDate = null;
                                                                                    }
                                                                                    setNonprofitOrganizations(updated);
                                                                                    updateNonprofitMutation.mutate({
                                                                                        id: nonprofit.id,
                                                                                        data: {
                                                                                            isCurrent: e.target.checked,
                                                                                            endDate: e.target.checked ? null : nonprofit.endDate
                                                                                        }
                                                                                    });
                                                                                }}
                                                                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                                            />
                                                                            <span className="text-sm text-gray-700">Current</span>
                                                                        </label>
                                                                    </div>

                                                                    <LogoUpload
                                                                        label="Logo"
                                                                        size="sm"
                                                                        value={nonprofit.logo || ''}
                                                                        onChange={(url) => saveNonprofitLogo(nonprofit, index, url)}
                                                                    />

                                                                    <input
                                                                        type="url"
                                                                        value={nonprofit.website || ''}
                                                                        onChange={(e) => {
                                                                            const updated = [...nonprofitOrganizations];
                                                                            updated[index].website = e.target.value;
                                                                            setNonprofitOrganizations(updated);
                                                                        }}
                                                                        onBlur={() => updateNonprofitMutation.mutate({
                                                                            id: nonprofit.id,
                                                                            data: { website: nonprofit.website }
                                                                        })}
                                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                                        placeholder="Organization website (optional)"
                                                                    />
                                                                </div>

                                                                <div className="flex items-start space-x-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            updateNonprofitMutation.mutate({
                                                                                id: nonprofit.id,
                                                                                data: { isVisible: !nonprofit.isVisible }
                                                                            });
                                                                        }}
                                                                        className="text-gray-500 hover:text-gray-700 mt-1"
                                                                    >
                                                                        {nonprofit.isVisible ? (
                                                                            <Eye className="h-5 w-5" />
                                                                        ) : (
                                                                            <EyeOff className="h-5 w-5" />
                                                                        )}
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        onClick={() => deleteNonprofitMutation.mutate(nonprofit.id)}
                                                                        className="text-red-500 hover:text-red-700 mt-1"
                                                                    >
                                                                        <X className="h-5 w-5" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>

                            {showNonprofitForm ? (
                                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                    <form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            const formData = new FormData(e.target);
                                            addNonprofitMutation.mutate({
                                                name: formData.get('name'),
                                                role: formData.get('role'),
                                                startDate: formData.get('startDate'),
                                                endDate: formData.get('endDate'),
                                                isCurrent: formData.get('isCurrent') === 'on',
                                                website: formData.get('website'),
                                                ...(newNonprofitLogo ? { logo: newNonprofitLogo } : {})
                                            });
                                        }}
                                        className="space-y-3"
                                    >
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <input
                                                type="text"
                                                name="name"
                                                required
                                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                placeholder="Organization name"
                                            />

                                            <input
                                                type="text"
                                                name="role"
                                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                                placeholder="Your role (optional)"
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            <input
                                                type="date"
                                                name="startDate"
                                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                            />

                                            <input
                                                type="date"
                                                name="endDate"
                                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                            />

                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    name="isCurrent"
                                                    defaultChecked
                                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-sm text-gray-700">Current</span>
                                            </label>
                                        </div>

                                        <input
                                            type="url"
                                            name="website"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                            placeholder="Organization website (optional)"
                                        />

                                        <LogoUpload label="Logo" size="sm" value={newNonprofitLogo} onChange={setNewNonprofitLogo} />

                                        <div className="flex justify-end space-x-3">
                                            <button
                                                type="button"
                                                onClick={() => { setShowNonprofitForm(false); setNewNonprofitLogo(''); }}
                                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={addNonprofitMutation.isPending}
                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                                            >
                                                {addNonprofitMutation.isPending ? (
                                                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                                ) : (
                                                    <Plus className="h-4 w-4 mr-2" />
                                                )}
                                                Add Organization
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setShowNonprofitForm(true)}
                                    className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Nonprofit Organization
                                </button>
                            )}
                        </>
                    )}
                </div>

                </div>

                {/* Desktop: live preview beside the form */}
                <aside className="hidden lg:block sticky top-6" aria-label="Live card preview">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-sm font-semibold text-gray-900">Live preview</h2>
                        {isDirty && (
                            <span className="text-xs font-medium text-orange-700 bg-orange-100 rounded-full px-2 py-0.5">Unsaved changes</span>
                        )}
                    </div>
                    <PhonePreview card={previewCard} width={300} />
                    <p className="mt-3 text-center text-xs text-gray-500">
                        Updates as you edit. Visitors see it after you save.
                    </p>
                </aside>
                </div>

                {/* Image Editor Modal */}
                {showImageEditor && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg max-w-md w-full p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Adjust Your Profile Picture
                            </h3>

                            <div className="flex flex-col items-center">
                                <div className="mb-4 border-2 border-gray-200 rounded-full">
                                    <AvatarEditor
                                        ref={editorRef}
                                        image={imageToEdit}
                                        width={250}
                                        height={250}
                                        border={0}
                                        borderRadius={125}
                                        color={[255, 255, 255, 0.6]} // RGBA
                                        scale={editorScale}
                                        position={editorPosition}
                                        onPositionChange={setEditorPosition}
                                        rotate={0}
                                    />
                                </div>

                                <div className="w-full mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Zoom
                                    </label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="3"
                                        step="0.01"
                                        value={editorScale}
                                        onChange={(e) => setEditorScale(parseFloat(e.target.value))}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                    />
                                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                                        <span>Zoom out</span>
                                        <span>Zoom in</span>
                                    </div>
                                </div>

                                <p className="text-sm text-gray-600 mb-6 text-center">
                                    Drag the image to reposition • Use the slider to zoom
                                </p>

                                <div className="flex space-x-3 w-full">
                                    <button
                                        onClick={() => {
                                            setShowImageEditor(false);
                                            setImageToEdit(null);
                                        }}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSaveEditedImage}
                                        disabled={uploadPictureMutation.isPending}
                                        className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                                    >
                                        {uploadPictureMutation.isPending ? (
                                            <>
                                                <Loader2 className="animate-spin h-4 w-4 mr-2 inline" />
                                                Saving...
                                            </>
                                        ) : (
                                            'Save Picture'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Profile;